import * as THREE from 'three';
import type { PlayerInfo, PlayerState } from '../../server/protocol.ts';
import type { Universe } from '../astro/Universe';
import { SHIP_BY_ID, SHIPS, type ShipColor } from '../ship/ShipCatalog';
import { ShipVisual } from '../ship/ShipVisual';

const INTERP_DELAY = 150; // ms behind server time

type Snapshot = { time: number; st: PlayerState };

export type Remote = {
  info: PlayerInfo;
  snaps: Snapshot[];
  visual: ShipVisual | null;
  visualKey: string;
  position: THREE.Vector3; // heliocentric, refreshed each frame
  quaternion: THREE.Quaternion;
  state: PlayerState | null;
  wasDead: boolean;
};

const tmpA = new THREE.Vector3();
const tmpB = new THREE.Vector3();
const qa = new THREE.Quaternion();
const qb = new THREE.Quaternion();

/** Other pilots: snapshot buffer → interpolated pose in this client's own ephemeris. */
export class RemotePlayers {
  readonly group = new THREE.Group();
  readonly players = new Map<string, Remote>();
  onDeath: (r: Remote) => void = () => {};

  constructor(private readonly universe: Universe) {}

  upsert(info: PlayerInfo, st?: PlayerState, time?: number): Remote {
    let r = this.players.get(info.id);
    if (!r) {
      r = { info, snaps: [], visual: null, visualKey: '', position: new THREE.Vector3(), quaternion: new THREE.Quaternion(), state: null, wasDead: false };
      this.players.set(info.id, r);
    }
    r.info = info;
    if (st && time) this.push(info.id, time, st);
    this.ensureVisual(r);
    return r;
  }

  private ensureVisual(r: Remote): void {
    const key = `${r.info.ship}:${r.info.color}`;
    if (r.visualKey === key) return;
    r.visualKey = key;
    const spec = SHIP_BY_ID.get(r.info.ship) ?? SHIPS[0];
    const v = new ShipVisual(spec, r.info.color as ShipColor);
    void v.ready.then(() => {
      if (r.visualKey !== key || !this.players.has(r.info.id)) return;
      if (r.visual) this.group.remove(r.visual.group);
      r.visual = v;
      this.group.add(v.group);
    });
  }

  remove(id: string): void {
    const r = this.players.get(id);
    if (r?.visual) this.group.remove(r.visual.group);
    this.players.delete(id);
  }

  push(id: string, time: number, st: PlayerState): void {
    const r = this.players.get(id);
    if (!r) return;
    const last = r.snaps[r.snaps.length - 1];
    if (last && time <= last.time) return;
    r.snaps.push({ time, st });
    if (r.snaps.length > 30) r.snaps.shift();
  }

  /** Heliocentric position for a frame-relative state at the current ephemeris. */
  toWorld(st: PlayerState, out: THREE.Vector3, q?: THREE.Quaternion): boolean {
    const body = this.universe.byId.get(st.f);
    if (!body) return false;
    out.set(st.p[0], st.p[1], st.p[2]);
    if (q) q.set(st.q[0], st.q[1], st.q[2], st.q[3]);
    if (st.r) {
      out.applyMatrix4(body.rotation);
      if (q) q.premultiply(body.quaternion);
    }
    out.add(body.position);
    return true;
  }

  update(dt: number, serverNow: number, origin: THREE.Vector3): void {
    const renderTime = serverNow - INTERP_DELAY;
    for (const r of this.players.values()) {
      const s = r.snaps;
      if (!s.length) continue;
      let a = s[0], b = s[s.length - 1];
      for (let i = s.length - 1; i > 0; i--) {
        if (s[i - 1].time <= renderTime) {
          a = s[i - 1];
          b = s[i];
          break;
        }
      }
      r.state = b.st;
      const okA = this.toWorld(a.st, tmpA, qa);
      const okB = this.toWorld(b.st, tmpB, qb);
      if (!okA || !okB) continue;
      if (a === b || a.st.f !== b.st.f || a.st.r !== b.st.r) {
        // Extrapolate briefly from the newest snapshot.
        const ahead = Math.min(0.5, Math.max(0, (renderTime - b.time) / 1000));
        r.position.copy(tmpB).addScaledVector(new THREE.Vector3(...b.st.v), ahead);
        r.quaternion.copy(qb);
      } else {
        const t = THREE.MathUtils.clamp((renderTime - a.time) / Math.max(1, b.time - a.time), 0, 1.5);
        r.position.lerpVectors(tmpA, tmpB, t);
        r.quaternion.slerpQuaternions(qa, qb, Math.min(1, t));
      }
      if (b.st.d && !r.wasDead) this.onDeath(r);
      r.wasDead = b.st.d;
      if (r.visual) {
        r.visual.group.visible = !b.st.d;
        r.visual.group.position.subVectors(r.position, origin);
        r.visual.group.quaternion.copy(r.quaternion);
        r.visual.setThrust(b.st.th, b.st.b, dt);
      }
    }
  }
}
